import { useCallback, useRef } from 'react';
import type { TableType } from '../grid/reduxStore/table.types';
import { TableContentCommand } from '../UndoRedo/TableContentCommand';
import { UndoRedoManagerCommandVersion } from '../UndoRedo/UndoRedoManagerCommandVersion';
import { useSocketClient } from '../../../providers/SocketContext';
import { useCurrentUser } from '../../../providers/UserProvider';
import { useAppDispatch } from '../grid/reduxStore/Store';
import { useCheckDocumentLockStatus } from 'components/editor/hooks/useCheckDocumentLockStatus';

export function useTableContentUndoRedo() {
  const dispatch = useAppDispatch();
  const { data } = useCurrentUser();
  const userId = data.id;
  const { socketClient } = useSocketClient();
  const checkDocumentLockStatus = useCheckDocumentLockStatus();
  const snapshotRef = useRef<TableType | null>(null);

  const captureBeforeEdit = useCallback((currentContent: TableType) => {
    snapshotRef.current = structuredClone(currentContent);
  }, []);

  const commitEdit = useCallback(
    (blockId: string, sectionId: string, newContent: TableType): void => {
      const previousContent = snapshotRef.current;
      snapshotRef.current = null;

      if (!previousContent) {
        console.warn('useTableContentUndoRedo: commitEdit called without captureBeforeEdit');
        return;
      }
      if (isTableContentEqual(previousContent, newContent)) return;

      const command = new TableContentCommand(
        { dispatch, socketClient, userId, checkDocumentLockStatus },
        { blockId, sectionId, previousContent, newContent: structuredClone(newContent) }
      );

      command.applyOptimistic();

      const undoRedoManager = UndoRedoManagerCommandVersion.getUndoRedoManager();
      undoRedoManager.pushUndoRedoCommands(command);

      command.persistToSocket().then((success) => {
        if (!success) {
          console.error('useTableContentUndoRedo: socket persist failed, state was rolled back');

          undoRedoManager.clearBlockCommands(blockId);
        }
      });
    },
    [dispatch, socketClient, userId, checkDocumentLockStatus]
  );

  const handleTableContentChanged = useCallback(
    (blockId: string, sectionId: string, previousContent: TableType, newContent: TableType): void => {
      if (isTableContentEqual(previousContent, newContent)) return;
      const command = new TableContentCommand(
        { dispatch, socketClient, userId, checkDocumentLockStatus },
        {
          blockId,
          sectionId,
          previousContent: structuredClone(previousContent),
          newContent: structuredClone(newContent),
        }
      );

      command.applyOptimistic();

      const undoRedoManager = UndoRedoManagerCommandVersion.getUndoRedoManager();
      undoRedoManager.pushUndoRedoCommands(command);

      command.persistToSocket().then((success) => {
        if (!success) {
          console.error('useTableContentUndoRedo: socket persist failed, state was rolled back');
          undoRedoManager.clearBlockCommands(blockId);
        }
      });
    },
    [dispatch, socketClient, userId, checkDocumentLockStatus]
  );

  return { captureBeforeEdit, commitEdit, handleTableContentChanged };
}

function isTableContentEqual(a: TableType, b: TableType): boolean {
  try {
    const normalizeColumn = (column: any) => ({
      field: column.field,
      headerName: column.headerName,
      width: column.width,
      columnType: column.columnType,
      minWidth: column.minWidth,
      maxWidth: column.maxWidth,
      flex: column.flex,
      editable: column.editable,
      sortable: column.sortable,
      type: column.type,
    });

    const normalize = (t: TableType) =>
      JSON.stringify({
        rows: t.rows,
        columns: t.columns.map(normalizeColumn),
        metadata: t.metadata,
        totalRows: t.totalRows,
      });
    return normalize(a) === normalize(b);
  } catch {
    return false;
  }
}
